import { Input } from '@iwoca/orion';
import { FieldError, UseFormRegister } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import styles from './ManualAddress.module.css';
import { supportsAutoAddress } from '../../helpers/address';
import { getCountry } from '../../iwIntlProvider/CountryProvider';
import { TPersonFormValues } from '../../myAccount/modals/personModal/PersonModal.types';
import { renderFieldError } from '../helpers/renderFieldError';

type ManualAddressRHFProps = {
    addressIndex: number;
    register: UseFormRegister<TPersonFormValues>;
    onManualAddressToggle: (addressIndex: number) => void;
    errors?: {
        house_number?: FieldError;
        street_line_1?: FieldError;
        street_line_2?: FieldError;
        town?: FieldError;
        postcode?: FieldError;
    };
};

export function ManualAddressRHF({
    addressIndex,
    register,
    onManualAddressToggle,
    errors
}: ManualAddressRHFProps) {
    const intl = useIntl();

    return (
        <div
            data-testid="manual-address"
            className={`${styles.ManualAddress} space-y-l`}
        >
            <div className={styles.twoColumnWrapper}>
                <Input
                    {...register(`addresses.${addressIndex}.house_number`)}
                    type="text"
                    data-testid="house-number-input"
                    wrapperClassName={styles.houseNumberField}
                    label={intl.formatMessage({
                        id: '_.manual_address.house_number',
                        defaultMessage: 'House number'
                    })}
                />

                <BuildingDetails
                    addressIndex={addressIndex}
                    register={register}
                />
            </div>

            <Input
                {...register(`addresses.${addressIndex}.street_line_1`)}
                type="text"
                data-testid="street-line-one-input"
                label={intl.formatMessage({
                    id: '_.manual_address.street_line_1',
                    defaultMessage: 'Street line 1'
                })}
            />

            <Input
                {...register(`addresses.${addressIndex}.street_line_2`)}
                type="text"
                label={intl.formatMessage({
                    id: '_.manual_address.street_line_2',
                    defaultMessage: 'Street line 2'
                })}
            />

            <div className={styles.twoColumnWrapper}>
                <Input
                    {...register(`addresses.${addressIndex}.town`)}
                    type="text"
                    data-testid="town-input"
                    label={intl.formatMessage({
                        id: '_.manual_address.town',
                        defaultMessage: 'Town'
                    })}
                    error={renderFieldError(errors?.town)}
                />

                <Input
                    {...register(`addresses.${addressIndex}.postcode`)}
                    type="text"
                    data-testid="post-code-input"
                    wrapperClassName={styles.postcodeField}
                    label={intl.formatMessage({
                        id: '_.manual_address.postcode',
                        defaultMessage: 'Postcode'
                    })}
                    error={renderFieldError(errors?.postcode)}
                />
            </div>

            {supportsAutoAddress() ? (
                <div
                    className={styles.manualAddressToggle}
                    onClick={() => onManualAddressToggle(addressIndex)}
                >
                    <FormattedMessage
                        id="_.manual_address.search_for_my_address"
                        defaultMessage="Search for my address"
                    />
                </div>
            ) : null}
        </div>
    );
}

function BuildingDetails({
    addressIndex,
    register
}: {
    addressIndex: number;
    register: UseFormRegister<TPersonFormValues>;
}) {
    const intl = useIntl();

    switch (getCountry()) {
        case 'UK':
            return (
                <Input
                    {...register(`addresses.${addressIndex}.house_name`)}
                    type="text"
                    wrapperClassName={styles.houseNameField}
                    label={intl.formatMessage({
                        id: '_.manual_address.house_name',
                        defaultMessage: 'House name'
                    })}
                />
            );
        case 'DE':
        default:
            return null;
    }
}
